import { Component, computed, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from "@angular/router";
import { paginatorConfig } from "@app/app.const";

import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "@components/filters/form-autocomplete/form-autocomplete.component";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { IApiResponse } from "@models/api-response.model";
import { Country } from "@models/country.model";
import { RequestService } from "@services/request/request.service";
import { formatCountryCode } from "../../../utils/country-code.util";
import { VendorEmailDialogComponent } from "./vendor-email-dialog.component";

/** A top-level category, as the categories endpoint returns it. */
interface CategoryOption {
  id: number;
  category_name: string;
}

/** One Category + Markup Percentage pair in the Markup Rules column. */
interface MarkupRule {
  /** A main category id, held as a string because that is what a <select> yields. */
  category: string;
  percentage: string;
}

/**
 * What a vendor with no rules yet shows: a single blank pair.
 *
 * A shared frozen constant rather than a fresh array per call - `markupRulesFor`
 * runs on every change-detection pass for every row, and returning a new array
 * each time would make `@for` re-diff the whole cell continuously.
 */
const BLANK_RULES: readonly MarkupRule[] = Object.freeze([
  Object.freeze({ category: "", percentage: "" }) as MarkupRule,
]);

@Component({
  selector: "fh-vendor-list",
  templateUrl: "./vendor-list.component.html",
  styleUrls: ["./vendor-list.component.css"],
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    MatButtonModule,
    FormAutocompleteComponent,
    FilterBarComponent,
    DataTableComponent,
    ReactiveFormsModule,
    RefreshButtonComponent,
    MatTooltipModule,
  ],
})
export class VendorListComponent implements OnInit {
  @ViewChild("markupRulesTemplate", { static: true })
  markupRulesTemplateRef!: TemplateRef<any>;

  /** Bound wrapper for the refresh button — see `getVendors()`. */
  refreshData = (): void => this.getVendors();
  // State signals
  iso2 = signal<string | null>(null);
  searchText = signal<string>("");
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortProp = signal<string>("created_on");
  sortDir = signal<"asc" | "desc">("desc");
  isLoading = signal<boolean>(false);

  /**
   * Markup Rules, keyed by vendor id.
   *
   * Deliberately client-side only: the column is for visibility while the shape
   * of the feature is decided, so nothing is loaded from the API and nothing is
   * sent back. Edits last as long as the page does.
   */
  private readonly markupRules = signal<Record<number, MarkupRule[]>>({});
  /** Vendor ids whose rules are mid-save, so the cell can show it. */
  readonly savingRules = signal<Set<number>>(new Set());
  /** Vendor ids whose last save failed. Shown in the cell, not as a toast: the
   *  control is per row, and a toast does not say which row did not save. */
  readonly failedRules = signal<Set<number>>(new Set());
  /** The server's reason for a failed save (e.g. "Markup out of range"), keyed by vendor id. */
  readonly failedRuleMessages = signal<Record<number, string>>({});

  // Data signals
  vendors = signal<any[]>([]);
  total = signal<number>(0);
  originCountries = signal<Country[]>([]);
  /** Top-level categories, for the Markup Rules category picker. */
  mainCategories = signal<CategoryOption[]>([]);
  zones = signal<Record<string, string>>({});

  // Computed
  countryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.originCountries().map((country) => ({
      value: country.iso2,
      label: country.name,
      count: 0,
    }));
  });
  countryFilterFormControl = new FormControl<string | null>(null);

  tableConfig = computed<TableConfig<any>>(() => ({
    columns: this.getTableColumns(),
    data: this.vendors(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: paginatorConfig.pageSizeOptions,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: true,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
    detailsRoute: "/vendor_input/:id",
    getDetailsRouteParams: (row) => ({ id: row.id }),
  }));

  tableActions = computed<TableAction<any>[]>(() => [
    {
      icon: "delete",
      label: "Delete Vendor",
      handler: (row, event) => this.delete(row.id, event),
      color: "warn",
    },
  ]);

  readonly paginatorConfig = paginatorConfig;

  constructor(
    private router: Router,
    private requestService: RequestService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.fetchOriginCountries();
    this.fetchMainCategories();
    this.activatedRoute.queryParams.subscribe((params) => {
      this.searchText.set(params.search || "");
      this.pageSize.set(+(params.pageSize || paginatorConfig.initialPageSize));
      this.pageIndex.set(+(params.page || 0));
      this.iso2.set(params.iso2 || null);
      this.sortProp.set(params.sortProp || "created_on");
      this.sortDir.set((params.sortDir || "desc") as "asc" | "desc");

      // for the first time when this page loads we need to patch value to form control
      this.countryFilterFormControl.patchValue(this.iso2() || null);

      this.getVendors();
    });
  }

  openSendEmailDialog(): void {
    this.dialog.open(VendorEmailDialogComponent, {
      width: "480px",
      data: {
        countryOptions: this.countryOptions().map((c) => ({ value: c.value, label: c.label })),
      },
    });
  }

  addVendor(): void {
    this.router.navigate(["/vendor_input/0"]);
  }

  onCountryChange(iso2: string | null): void {
    this.updateQueryParams({ iso2: iso2 || undefined, page: 0 });
  }

  onSearchChange(searchText: string): void {
    this.updateQueryParams({ search: searchText || undefined, page: 0 });
  }

  onResetFilters(): void {
    this.updateQueryParams({
      iso2: undefined,
      search: undefined,
      page: 0,
    });
  }

  onSortChange(event: TableSortEvent): void {
    this.updateQueryParams({
      sortProp: event.prop || undefined,
      sortDir: event.dir || undefined,
      page: 0,
    });
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  private fetchOriginCountries(): void {
    this.requestService.get<IApiResponse<Country[]>>("/api/v2/origin-country-list").subscribe({
      next: (res) => {
        if (res.status === true && res.data) {
          this.originCountries.set(res.data);
        }
      },
      error: (error) => {
        console.error("Error fetching origin countries:", error);
      },
    });
  }

  /**
   * Top-level categories only - parent_id 0 is the main level, and the explicit
   * pageSize is because the endpoint defaults to 25, which would quietly drop
   * options off the end of the list.
   */
  private fetchMainCategories(): void {
    this.requestService
      .get<IApiResponse>("/api/v2/categories?parent_id=0&status=active&pageSize=500")
      .subscribe({
        next: (res) => this.mainCategories.set(this.categoriesFrom(res)),
        error: () => this.mainCategories.set([]),
      });
  }

  /** The categories endpoint returns either a bare array or {categories}; accept both. */
  private categoriesFrom(res: IApiResponse): CategoryOption[] {
    const data = res?.data as unknown;
    if (Array.isArray(data)) return data as CategoryOption[];
    const inner = (data as { categories?: CategoryOption[] })?.categories;
    return Array.isArray(inner) ? inner : [];
  }

  private getVendors(): void {
    const searchText = this.searchText();
    let apiUrl = `/api/v2/vendor?pageSize=${this.pageSize()}&pageIndex=${this.pageIndex()}&search=${searchText}`;

    if (this.iso2()) {
      apiUrl += `&iso2=${this.iso2()}`;
    }

    if (this.sortProp() && this.sortDir()) {
      apiUrl += `&sortProp=${this.sortProp()}&sortDir=${this.sortDir()}`;
    }

    this.isLoading.set(true);
    this.requestService.get(apiUrl).subscribe({
      next: (res: any) => {
        if (res.status === true) {
          const vendors = res.data.list || [];
          this.total.set(res.data.count || 0);

          // Seed the Markup Rules cells from what the list already carries, so a
          // saved rule is still there after a refresh, a re-filter or a sort.
          const seeded: Record<number, MarkupRule[]> = {};
          for (const v of vendors) {
            const saved = Array.isArray(v.markup_rules) ? v.markup_rules : [];
            if (saved.length) {
              seeded[v.id] = saved.map((r: { categoryId: number; markup: string | number }) => ({
                category: String(r.categoryId),
                percentage: String(Number(r.markup)),
              }));
            }
          }
          this.markupRules.set(seeded);

          if (vendors.length > 0) {
            this.get_zones(vendors);
          }

          this.vendors.set(vendors);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.vendors.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private get_zones(vendors: any[]): void {
    const zoneMap: Record<string, string> = {};
    vendors.forEach((vendor) => {
      if (vendor.delivery_info && vendor.delivery_info.length > 0) {
        vendor.delivery_info.forEach((delivery: any) => {
          if (!zoneMap[delivery.deliveryZoneId]) {
            zoneMap[delivery.deliveryZoneId] = delivery.zone_name;
          }
        });
      }
    });
    this.zones.set(zoneMap);
  }

  private delete(id: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (!confirm("Are you sure you want to delete this vendor?")) {
      return;
    }

    this.requestService.delete(`/api/v2/vendor/${id}`).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.getVendors();
        }
      },
    });
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  /** This vendor's pairs, or a single blank one when it has none. */
  markupRulesFor(vendorId: number): readonly MarkupRule[] {
    return this.markupRules()[vendorId] ?? BLANK_RULES;
  }

  addRule(vendorId: number): void {
    this.updateRules(vendorId, (rules) => [...rules, { category: "", percentage: "" }]);
  }

  removeRule(vendorId: number, index: number): void {
    // Removing saves on its own, so a filled-in rule asks first; a blank one just goes.
    const rule = this.markupRulesFor(vendorId)[index];
    if ((rule?.category || rule?.percentage) && !confirm("Remove this markup rule?")) return;
    this.updateRules(vendorId, (rules) => rules.filter((_, i) => i !== index));
  }

  setRuleCategory(vendorId: number, index: number, category: string): void {
    this.updateRules(vendorId, (rules) =>
      rules.map((rule, i) => (i === index ? { ...rule, category } : rule))
    );
  }

  setRulePercentage(vendorId: number, index: number, percentage: string): void {
    this.updateRules(vendorId, (rules) =>
      rules.map((rule, i) => (i === index ? { ...rule, percentage } : rule))
    );
  }

  /**
   * Replaces one vendor's list. Removing the last pair leaves a blank one behind
   * rather than an empty cell with no way to start again.
   */
  private updateRules(
    vendorId: number,
    change: (rules: readonly MarkupRule[]) => MarkupRule[]
  ): void {
    const all = this.markupRules();
    const next = change(all[vendorId] ?? BLANK_RULES);
    const settled = next.length ? next : [{ category: "", percentage: "" }];
    this.markupRules.set({ ...all, [vendorId]: settled });
    this.saveRules(vendorId, settled);
  }

  /**
   * Writes the vendor's finished list to the server.
   *
   * Debounced per vendor because the percentage field saves on every keystroke -
   * typing "15" would otherwise send 1 and then 15, and the first request could
   * land second. Blank pairs are sent as they are; the server drops them.
   */
  private readonly saveTimers = new Map<number, ReturnType<typeof setTimeout>>();

  private saveRules(vendorId: number, rules: MarkupRule[]): void {
    clearTimeout(this.saveTimers.get(vendorId));
    this.saveTimers.set(
      vendorId,
      setTimeout(() => {
        const payload = rules
          .filter((r) => r.category !== "" && r.percentage !== "")
          .map((r) => ({ categoryId: Number(r.category), markup: Number(r.percentage) }));

        this.savingRules.update((s) => new Set(s).add(vendorId));
        this.requestService
          .put<IApiResponse>(`/api/v2/vendor/${vendorId}/markup-rules`, { rules: payload })
          .subscribe({
            next: (res) => {
              // The server answers 200 with status false for a rejected save.
              if (!res?.status) {
                this.rulesSaveFailed(vendorId, res?.message, res);
                return;
              }
              this.savingRules.update((s) => {
                const n = new Set(s);
                n.delete(vendorId);
                return n;
              });
              this.failedRules.update((s) => {
                const n = new Set(s);
                n.delete(vendorId);
                return n;
              });
            },
            error: (err: unknown) => this.rulesSaveFailed(vendorId, undefined, err),
          });
      }, 600)
    );
  }

  private rulesSaveFailed(vendorId: number, message: string | undefined, err: unknown): void {
    this.savingRules.update((s) => {
      const n = new Set(s);
      n.delete(vendorId);
      return n;
    });
    console.error("Failed to save markup rules:", err);
    this.failedRuleMessages.update((m) => ({ ...m, [vendorId]: message || "" }));
    this.failedRules.update((s) => new Set(s).add(vendorId));
  }

  private getTableColumns(): TableColumn<any>[] {
    return [
      {
        key: "id",
        header: "ID",
        sortable: true,
        width: "80px",
      },
      {
        key: "profile_pic",
        header: "Profile",
        sortable: false,
        width: "100px",
        type: "image",
      },
      {
        key: "name",
        header: "Name",
        sortable: true,
        width: "200px",
      },
      {
        key: "email",
        header: "Email",
        sortable: true,
        width: "200px",
      },
      {
        key: "phone_country_code",
        header: "Country Code",
        sortable: false,
        width: "100px",
      },
      {
        key: "phone_number",
        header: "Phone",
        sortable: false,
        width: "140px",
      },
      {
        key: "is_star_seller",
        header: "Star Seller",
        sortable: false,
        width: "120px",
        type: "boolean",
      },
      {
        key: "status",
        header: "Status",
        sortable: false,
        width: "100px",
        cssClass: (row) => `status-${row.status}`,
      },
      {
        key: "active_product_count",
        header: "# Active Products",
        sortable: true,
        width: "140px",
      },
      {
        key: "iso2",
        header: "Country",
        sortable: true,
        width: "100px",
        format: formatCountryCode,
      },
      {
        key: "markup_rules",
        header: "Markup Rules",
        sortable: false,
        width: "300px",
        minWidth: "280px",
        template: this.markupRulesTemplateRef,
      },
      {
        key: "created_on",
        header: "Created On",
        sortable: true,
        width: "140px",
        type: "date",
      },
    ];
  }
}
